import isNumber from "lodash/isNumber";
import {
    RuleActionType,
    RuleConditionAmount,
    RuleConditionType,
    TransactionRuleCondition,
} from "../../../../common/types/transactionRule";
import {
    AmountConditionType,
    TransactionRuleConditionFormSchema,
    DescriptionConditionType,
    FormRuleConditionType,
    TransactionRuleActionFormSchema,
    FormRuleActionType,
} from "./constants";

export function conditionFormRowToModel(
    condition: TransactionRuleConditionFormSchema,
): Omit<TransactionRuleCondition, "id"> | undefined {
    switch (condition.conditionType) {
        case FormRuleConditionType.CATEGORY:
            return buildCategoryConditionFromFormRow(condition);
        case FormRuleConditionType.SUGGESTED_CATEGORY:
            return buildSuggestedCategoryConditionFromFormRow(condition);
        case FormRuleConditionType.AMOUNT:
            return buildAmountConditionFromFormRow(condition);
        case FormRuleConditionType.BANK_DESCRIPTION:
            return buildDescriptionConditionFromFormRow(condition);
        case FormRuleConditionType.ACCOUNT:
            return buildAccountConditionFromFormRow(condition);
        case FormRuleConditionType.DATE:
            return buildDateConditionFromFormRow(condition);
        case FormRuleConditionType.ENTITY:
            return buildEntityConditionFromFormRow(condition);
        case FormRuleConditionType.COUNTERPARTY:
            return buildCounterpartyConditionFromFormRow(condition);
    }
}

function buildCategoryConditionFromFormRow(
    condition: TransactionRuleConditionFormSchema,
): Omit<TransactionRuleCondition, "id"> | undefined {
    if (condition.categoryIds?.length) {
        return {
            conditionType: RuleConditionType.CATEGORY,
            value: condition.categoryIds,
        };
    }
}

function buildSuggestedCategoryConditionFromFormRow(
    condition: TransactionRuleConditionFormSchema,
): Omit<TransactionRuleCondition, "id"> | undefined {
    if (condition.categoryIds?.length) {
        return {
            conditionType: RuleConditionType.SUGGESTED_CATEGORY,
            value: condition.categoryIds,
        };
    }
}

function buildAmountConditionFromFormRow(
    condition: TransactionRuleConditionFormSchema,
): Omit<TransactionRuleCondition, "id"> {
    const value: RuleConditionAmount = {};

    if (
        condition.amountConditionType === AmountConditionType.BETWEEN &&
        isNumber(condition.amountValueRange?.min) &&
        isNumber(condition.amountValueRange?.max)
    ) {
        value.min = condition.amountIsCredit
            ? condition.amountValueRange.min
            : -condition.amountValueRange.max;
        value.max = condition.amountIsCredit
            ? condition.amountValueRange.max
            : -condition.amountValueRange.min;
    }

    if (
        condition.amountConditionType === AmountConditionType.GREATER_THAN &&
        isNumber(condition.amountSingleValue)
    ) {
        if (condition.amountIsCredit) {
            value.min = condition.amountSingleValue;
        } else {
            value.max = -condition.amountSingleValue;
        }
    }

    if (
        condition.amountConditionType === AmountConditionType.LESS_THAN &&
        isNumber(condition.amountSingleValue)
    ) {
        if (condition.amountIsCredit) {
            value.min = 0;
            value.max = condition.amountSingleValue;
        } else {
            value.min = -condition.amountSingleValue;
            value.max = 0;
        }
    }

    if (
        condition.amountConditionType === AmountConditionType.IS &&
        isNumber(condition.amountSingleValue)
    ) {
        if (condition.amountIsCredit) {
            value.min = condition.amountSingleValue;
            value.max = condition.amountSingleValue;
        } else {
            value.min = -condition.amountSingleValue;
            value.max = -condition.amountSingleValue;
        }
    }

    return {
        conditionType: RuleConditionType.AMOUNT,
        value,
    };
}

function buildDescriptionConditionFromFormRow(
    condition: TransactionRuleConditionFormSchema,
): Omit<TransactionRuleCondition, "id"> | undefined {
    if (condition.descriptionValue) {
        return {
            conditionType:
                condition.descriptionConditionType ===
                DescriptionConditionType.IS
                    ? RuleConditionType.DESCRIPTION_IS
                    : RuleConditionType.DESCRIPTION_INCLUDES,
            value: condition.descriptionValue,
        };
    }
}

function buildAccountConditionFromFormRow(
    condition: TransactionRuleConditionFormSchema,
): Omit<TransactionRuleCondition, "id"> | undefined {
    if (condition.accountValue?.length) {
        return {
            conditionType: RuleConditionType.FINANCIAL_ACCOUNTS,
            value: condition.accountValue,
        };
    }
}

function buildDateConditionFromFormRow(
    condition: TransactionRuleConditionFormSchema,
): Omit<TransactionRuleCondition, "id"> | undefined {
    if (condition.dateValueRange?.after || condition.dateValueRange?.before) {
        // here we check if they are in the correct "order", and if not, we can swap them
        if (condition.dateValueRange.after && condition.dateValueRange.before) {
            if (
                new Date(condition.dateValueRange.after) >
                new Date(condition.dateValueRange.before)
            ) {
                const temp = condition.dateValueRange.after;
                condition.dateValueRange.after =
                    condition.dateValueRange.before;
                condition.dateValueRange.before = temp;
            }
        }
        return {
            conditionType: RuleConditionType.DATE,
            value: condition.dateValueRange,
        };
    }
}

function buildEntityConditionFromFormRow(
    condition: TransactionRuleConditionFormSchema,
): Omit<TransactionRuleCondition, "id"> | undefined {
    if (condition.entityValue) {
        return {
            conditionType: RuleConditionType.ENTITY,
            value: condition.entityValue,
        };
    }
}

function buildCounterpartyConditionFromFormRow(
    condition: TransactionRuleConditionFormSchema,
): Omit<TransactionRuleCondition, "id"> | undefined {
    if (condition.counterpartyIds?.length) {
        return {
            conditionType: RuleConditionType.COUNTERPARTY,
            value: condition.counterpartyIds,
        };
    }
}

export function formActionsToModel(
    actions: TransactionRuleActionFormSchema[],
): Array<{
    value: string | number | string[];
    actionType: RuleActionType;
    order: number;
}> {
    const actionResults = actions
        .filter(
            (a): a is Required<TransactionRuleActionFormSchema> =>
                !!a.actionType && !!a.value,
        )
        .map((action, index) => {
            switch (action.actionType) {
                case FormRuleActionType.CATEGORY:
                    return {
                        value: action.value.categoryId,
                        actionType: RuleActionType.CATEGORY,
                        order: index,
                    };
                case FormRuleActionType.ENTITY:
                    return {
                        value: action.value.entityId,
                        actionType: RuleActionType.ENTITY,
                        order: index,
                    };
                case FormRuleActionType.ACCOUNT_ID:
                case FormRuleActionType.ACCOUNT_NAME:
                    if (action.value.accountId) {
                        return {
                            value: action.value.accountId,
                            actionType: RuleActionType.ACCOUNT_ID,
                            order: index,
                        };
                    }
                    if (action.value.accountName) {
                        return {
                            value: action.value.accountName,
                            actionType: RuleActionType.ACCOUNT_NAME,
                            order: index,
                        };
                    }
                    break;
                case FormRuleActionType.CLASS:
                    return {
                        value: action.value?.classIds as string[],
                        actionType: RuleActionType.CLASS,
                        order: index,
                    };
                case FormRuleActionType.MEMO:
                    return {
                        value: action.value?.memo as string,
                        actionType: RuleActionType.MEMO,
                        order: index,
                    };
                case FormRuleActionType.COUNTERPARTY:
                    return {
                        value: action.value?.counterpartyId as string,
                        actionType: RuleActionType.COUNTERPARTY,
                        order: index,
                    };
            }
        })
        .filter(
            (action) =>
                !!action?.value &&
                (!Array.isArray(action.value) || action.value.length),
        ) as Array<{
        value: string | number | string[];
        actionType: RuleActionType;
        order: number;
    }>;

    return actionResults;
}
